import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCoaches from "./pages/AdminCoaches";
import CoachDashboard from "./pages/CoachDashboard";
import CoacheesList from "./pages/CoacheesList";
import CoacheeCreate from "./pages/CoacheeCreate";
import CoacheeDetail from "./pages/CoacheeDetail";
import CoacheeEdit from "./pages/CoacheeEdit";
import SessionCreate from "./pages/SessionCreate";
import SessionDetail from "./pages/SessionDetail";
import SessionEdit from "./pages/SessionEdit";
import Documents from "./pages/Documents";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.rol !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.rol === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/coaches" element={
        <ProtectedRoute adminOnly>
          <AdminCoaches />
        </ProtectedRoute>
      } />

      {/* Coach Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CoachDashboard />
        </ProtectedRoute>
      } />
      <Route path="/coachees" element={
        <ProtectedRoute>
          <CoacheesList />
        </ProtectedRoute>
      } />
      <Route path="/coachees/new" element={
        <ProtectedRoute>
          <CoacheeCreate />
        </ProtectedRoute>
      } />
      <Route path="/coachees/:coacheeId" element={
        <ProtectedRoute>
          <CoacheeDetail />
        </ProtectedRoute>
      } />
      <Route path="/coachees/:coacheeId/edit" element={
        <ProtectedRoute>
          <CoacheeEdit />
        </ProtectedRoute>
      } />
      <Route path="/coachees/:coacheeId/sessions/new" element={
        <ProtectedRoute>
          <SessionCreate />
        </ProtectedRoute>
      } />
      <Route path="/coachees/:coacheeId/sessions/:sessionId" element={
        <ProtectedRoute>
          <SessionDetail />
        </ProtectedRoute>
      } />
      <Route path="/coachees/:coacheeId/sessions/:sessionId/edit" element={
        <ProtectedRoute>
          <SessionEdit />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Documents />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
